import * as React from 'react';
import { Route, Redirect, RouteProps } from 'react-router-dom';
import { useAppSelector } from '../../app/hooks';
import { Role } from '../../common/constants';
import ErrorCode from '../../pages/errorCode';

interface ProtectedRouteProps extends RouteProps {
  component?: any;
  roles?: Role[];
  exceptRoles?: Role[];
}

enum AuthState {
  Authorized = 'Authorized',
  Unauthorized = 'Unauthorized',
  Unauthenticated = 'Unauthenticated',
}

const GetAuthState = (roles?: Role[], exceptRoles?: Role[]): AuthState => {
  const userRoles = useAppSelector((s) => s.user.roles);

  const isAuthenticated = useAppSelector(
    (s) => s.user.status === 'authenticated'
  );

  // User must be logged in first.
  if (!isAuthenticated) return AuthState.Unauthenticated;

  // No roles were applied so authentication is good enough.
  if (!roles) return AuthState.Authorized;

  let exceptRoleIsPresent = exceptRoles?.some(role => {
    return userRoles?.includes(role.toString());
  });

  if (exceptRoleIsPresent)
    return AuthState.Unauthorized;

  // Check if user has required role.
  const hasRole = userRoles?.includes(Role.SuperUser.toString()) || roles.some((role) => {
    return userRoles?.includes(role.toString());
  });

  return hasRole ? AuthState.Authorized : AuthState.Unauthorized;
};

const ProtectedRoute = (props: ProtectedRouteProps) => {
  const { roles, component: Component, children, exceptRoles, ...rest } = props;
  const currentAuthState = GetAuthState(roles, exceptRoles);

  return (
    <Route
      {...rest}
      render={(p) => {
        switch (currentAuthState) {
          case AuthState.Authorized:
            return Component === undefined ? children : <Component {...p} />;
          case AuthState.Unauthorized:
            return (
              <ErrorCode
                code='401'
                message="You don't have permission to view this page."
                to='/'
              />
            );
          default:
            return (
              <Redirect
                to={{
                  pathname: '/login',
                }}
              />
            );
        }
      }}
    />
  );
};

export default ProtectedRoute;
